/**
 * „AMIT EDDIG FELTÖLTÖTTEM" — nyugta ÉS visszakeresés, egy szakaszban.
 *
 * NEM NEGYEDIK FELÜLET, és nem is második az ügyfélnek *(Viktor, 2026-09-04)*.
 * Ugyanaz a szakasz a munkaképernyő alján, csak nem ér véget a huszadik
 * sornál: „az archív csak annyi, hogy vissza bírják nézni a régen elküldött
 * fájlokat". A makett (`design/Archivum.dc.html`) ezt külön oldalként rajzolja
 * — saját fejléccel, keresővel, hónap-elválasztókkal —, és AZ nyitna új
 * tengelyt. A visszakeresés maga nem.
 *
 * ⚠️ A JELVÉNY SZÍNTELEN, ÉS EZ NEM KIHAGYÁS. A `docs/design-tokens.md`
 * két külön szabályt mond ki, és a makett zöld „Megvan" jelvénye MINDKETTŐT
 * megszegi: a `--clear` „a listában SOHA", és „a státusz és a feldolgozottság
 * szándékosan NEM kap színt". Ezért a két állapot IKONNAL és SÚLLYAL válik el,
 * a jelvény háttere pedig mindkettőnél a semleges `--chip`. Ha ez színt kap,
 * a zöld elkopik, és a nulla-állapot — az egyetlen hely, ahol valamit
 * JELENT — ugyanolyan zöld lesz, mint kétszáz nyugta-sor.
 *
 * AMIT A SOR MOND, AZ IGAZ, ÉS NEM TÖBB. A státusz a KÖNYVELŐI rétegből jön
 * (`H11`); a rendszer feldolgozottsága nem megy ki, mert magától mozogna, és
 * az ügyfél azt látná, hogy „történt valami", pedig nem döntött róla senki.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileIcon, ImageIcon } from '@/components/common';
import { formatAmount, formatDateTime } from '@/i18n/generated';
import { DocTypePicker } from './DocTypePicker';
import { openUploadedFile } from './openUploadedFile';
import { splitFilename } from './splitFilename';
import type { ClientDocType, UploadedDocument, UploadStatus } from './types';

/**
 * A státusz felirata. KIMERÍTŐ `switch`, és a kulcsok KIÍRVA — egy
 * `t(`archive.status.${status}`)` sablonos hívást az i18n-kapu statikusan
 * nem lát, tehát egy hiányzó kulcs nyers kulcsként jelenne meg a felületen,
 * és semmilyen build nem bukna el tőle.
 */
function statusLabel(t: (key: string) => string, status: UploadStatus): string {
  switch (status) {
    case 'FILED':
      return t('archive.status.FILED');
    default:
      // Az `ARRIVED` az alapértelmezés is: a hálózatról jövő ISMERETLEN
      // érték sosem ígérhet többet a valóságnál. A szerver ugyanezt teszi
      // (`statusForClient`) — a két oldal ugyanabba az irányba téved.
      return t('archive.status.ARRIVED');
  }
}

function StatusBadge({ status }: { status: UploadStatus }) {
  const { t } = useTranslation();
  const filed = status === 'FILED';

  return (
    <span className={filed ? 'receipt-badge is-filed' : 'receipt-badge'}>
      <span aria-hidden="true">{filed ? '✓' : '⋯'}</span>
      {statusLabel(t, status)}
    </span>
  );
}

/**
 * HÁNY SOR ÁLL A NYITÓKÉPEN. Öt: ennyi fér a bekérés alá egy 844px magas
 * telefonon görgetés nélkül, és ennyi elég ahhoz, hogy a MOST feltöltött
 * papír visszaigazolása látszódjon — ez a lista fő dolga.
 */
const FIRST_WINDOW = 5;

export function UploadReceipts({
  uploads,
  hasMore,
  loadingMore,
  docTypes,
  onLoadMore,
  onDeclared,
}: {
  uploads: UploadedDocument[];
  hasMore: boolean;
  loadingMore: boolean;
  /** A cég engedélyezett típusai — lásd `UploadPage.docTypes`. */
  docTypes: ClientDocType[] | null;
  onLoadMore: () => void;
  onDeclared: (documentId: string, docType: ClientDocType) => void;
}) {
  const { t } = useTranslation();
  const [failedId, setFailedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (uploads.length === 0) {
    return null;
  }

  /**
   * A NYITÓKÉPEN CSAK A LEGUTÓBBI ÖT ÁLL (#26/#44).
   *
   * Viktor döntése artboardon (2026-09-15, „C" változat): az ügyfél
   * nyitóképe a KÉRÉSRŐL szól, nem az archívumról. Húsz sor ott elnyomta a
   * bekérést — épp azt, amiért a képernyő van.
   *
   * ⚠️ A SZERVER LAPMÉRETE VÁLTOZATLAN (20). A szűkítés MEGJELENÍTÉSI, nem
   * adatkérési: a `document-archive.service.ts` húszas adagja saját, kimért
   * döntés („ennyi az, amit görgetés nélkül végig lehet nézni"), és azt egy
   * felületi kérdés nem írhatja felül. Cserébe az első „Régebbiek" INGYEN
   * van — a sorok már itt vannak, csak nem látszanak.
   */
  const visible = expanded ? uploads : uploads.slice(0, FIRST_WINDOW);
  const hiddenLoaded = uploads.length - visible.length;

  return (
    <section className="receipts">
      <h2>{t('archive.title')}</h2>

      <ul>
        {visible.map((upload) => (
          <li key={upload.id}>
            <FileThumb name={upload.originalFilename} />
            <span className="receipt-text">
              <ReadingLines upload={upload} />
              <span className="meta">
                {t(upload.uploadedOnBehalf ? 'archive.uploadedByOffice' : 'archive.uploadedAt', {
                  time: formatDateTime(upload.uploadedAt),
                })}
              </span>
            </span>
            {/* ⚠️ A KÉRDÉS CSAK OTT ÁLL, AHOL MÉG SZÁMÍT. A lefűzött
                bizonylatnál a könyvelő már döntött — megkérdezni az ügyfelet
                egy lezárt papírról egyszerre fölösleges és félrevezető: azt
                sugallná, hogy a válasza még számít. Egy kétszáz soros
                archívumban ez a szabály tartja a listát olvashatónak. */}
            <div className="receipt-controls">
              {upload.status === 'ARRIVED' ? (
                <DocTypePicker
                  documentId={upload.id}
                  declared={upload.clientDocType}
                  allowed={docTypes}
                  onDeclared={onDeclared}
                />
              ) : null}
              <StatusBadge status={upload.status} />
            </div>
            <button
              type="button"
              className="pick pick-link receipt-open"
              onClick={() => {
                setFailedId(null);
                openUploadedFile(upload.id).catch(() => setFailedId(upload.id));
              }}
            >
              {t('archive.open')}
            </button>
            {failedId === upload.id ? (
              <span className="error receipt-error" role="alert">
                {t('error.unexpected')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      {/* A GOMB AKKOR IS OTT MARAD, HA A LAPOZÁS ELBUKOTT. Egy hálózati hiba
          nem bizonyítja, hogy nincs több — eltüntetve az ügyfél azt hinné,
          ennyi az archívuma, és épp a régi fájljait nem találná. */}
      {/* KÉT LÉPCSŐ, EGY GOMB. Előbb a MÁR BETÖLTÖTT, de elrejtett sorokat
          fedi fel — ez azonnali, és a darabszámot is meg tudjuk mondani —,
          és csak utána kér új lapot a szervertől. Két külön gomb ugyanezt két
          fogalommá bontaná szét, holott az ügyfélnek egy kérdése van:
          „mutasd a régebbieket". */}
      {hiddenLoaded > 0 ? (
        <button type="button" className="pick pick-link older" onClick={() => setExpanded(true)}>
          {t('archive.olderCount', { count: hiddenLoaded })}
        </button>
      ) : hasMore ? (
        <button
          type="button"
          className="pick pick-link older"
          onClick={onLoadMore}
          disabled={loadingMore}
        >
          {loadingMore ? t('archive.loadingOlder') : t('archive.older')}
        </button>
      ) : null}
    </section>
  );
}

/**
 * A SOR CÍME ÉS ALÁRÁSA — a kiolvasás három állapota (#84).
 *
 * Viktor döntése artboardon (2026-11-01, `a-kiolvasott-nev-es-tipus.html`
 * 1. szakasz): a cím a kiolvasott PARTNER, alatta SZÁMLASZÁM · ÖSSZEG, a
 * fájlnév kisebben, harmadik sorban — az ügyfél a „17899920… 37241518.jpg"
 * alakról nem ismer rá a saját papírjára (#57, 10-30-i tesztkör).
 *
 *   olvasás közben  fájlnév + „Most olvassuk ki"
 *   kiolvasva       partner / számlaszám · összeg / fájlnév
 *   nincs mit / gyenge   fájlnév, ahogy eddig
 *
 * ⚠️ A GYENGESÉGET ITT NEM ÍTÉLJÜK MEG. A szerver a gyenge kiolvasást `null`-ként
 * adja (`client-reading.ts`), tehát ami ide megérkezik, az kiírható. Egy
 * második, kliensoldali küszöb csak elcsúszni tudna a szerveritől.
 *
 * A NÉV NEM FORDUL (#83): úgy áll, ahogy a számlán.
 */
function ReadingLines({ upload }: { upload: UploadedDocument }) {
  const { t } = useTranslation();

  if (upload.reading === 'READING') {
    return (
      <>
        <FileName name={upload.originalFilename} />
        <span className="receipt-reading">
          <span className="receipt-reading__bars" aria-hidden="true">
            <i />
            <i />
          </span>
          {t('archive.reading')}
        </span>
      </>
    );
  }

  const amount =
    upload.grossAmount != null && Number.isFinite(Number(upload.grossAmount))
      ? formatAmount(Number(upload.grossAmount), { currency: upload.currency ?? undefined })
      : null;
  const facts = [upload.invoiceNumber, amount].filter((fact): fact is string => !!fact);
  const sub =
    facts.length > 0 ? <span className="receipt-sub num">{facts.join(' · ')}</span> : null;

  if (!upload.partnerName) {
    return (
      <>
        <FileName name={upload.originalFilename} />
        {sub}
      </>
    );
  }

  return (
    <>
      <span className="receipt-partner">{upload.partnerName}</span>
      {sub}
      <span className="receipt-file">
        <FileName name={upload.originalFilename} />
      </span>
    </>
  );
}

/**
 * A FÁJLNÉV, KÖZÉPEN CSONKOLVA (#26/#36).
 *
 * KÉT DOBOZ, ÉS MINDKETTŐ KELL. Az első rugalmas, tehát ő kapja az
 * ellipszist; a második `flex: none`, tehát SOSEM zsugorodik — ezért marad
 * látható a név vége a kiterjesztéssel együtt. Egyetlen dobozzal a CSS csak a
 * végét tudná levágni, és két beolvasott papír ugyanúgy nézne ki.
 *
 * A `title` a TELJES nevet viszi: asztalon elemleírásként előjön, és a
 * képernyőolvasó is ezt mondja be — a csonkolás így csak VIZUÁLIS, nem
 * információvesztés.
 */
function FileName({ name }: { name: string }) {
  const { head, tail } = splitFilename(name);

  if (tail === '') {
    return (
      <span className="filename" title={name}>
        {name}
      </span>
    );
  }

  return (
    <span className="filename filename--split" title={name}>
      <span className="filename__head">{head}</span>
      <span className="filename__tail">{tail}</span>
    </span>
  );
}

/**
 * A SOR BÉLYEGE — a fájl FAJTÁJA, nem a tartalma (#57).
 *
 * Ákos demója valódi beolvasott képet rajzol ide. Ahhoz a szervernek
 * előnézetet kellene adnia, és minden sor egy újabb letöltés lenne egy
 * mobilhálózaton; a #57 a kinézetről szól, a működéshez nem nyúl. Az ikon
 * azt mondja meg, amit a fájlnév végéről is ki lehet olvasni — PDF-e vagy
 * fénykép —, de egy pillantásra.
 */
function FileThumb({ name }: { name: string }) {
  const isPdf = /\.pdf$/i.test(name);

  return (
    <span className={`receipt-thumb receipt-thumb--${isPdf ? 'pdf' : 'image'}`} aria-hidden="true">
      {isPdf ? <FileIcon /> : <ImageIcon />}
    </span>
  );
}
